/** Evolution Tree codex: SVG tree + detail panel + rotating 3D preview of the selected form. */
import type { Game } from '@core/Game';
import { ALL_STAGES, TREE, type StageId, ENDING_STAGES } from '@core/tree';
import { el, escapeHtml, fmtTime } from '../dom';
import { BRANCH_ICONS, ICONS } from '../icons';
import { navButton, type MenuNav } from '../nav';
import { PedestalScene } from '../three/PedestalScene';
import { buildTreeSvg } from './treeSvg';

export interface CodexHost {
  game: Game;
  nav: MenuNav;
  pushAux(view: { scene: import('three').Scene; camera: import('three').Camera; update?(dt: number): void }): void;
  popAux(): void;
  onClose(): void;
  /** Start a new run at a discovered form. */
  startAt(id: StageId): void;
  /** Stage explorer on → every form is known + playable. */
  explorer(): boolean;
}

export class Codex {
  readonly root = el('div', 'screen modal codex');
  private pedestal?: PedestalScene;
  private detail = el('div', 'codex-detail');
  private selected?: StageId;
  private isOpen = false;

  constructor(private readonly host: CodexHost) {}

  get open(): boolean {
    return this.isOpen;
  }

  /** Forms shown as known: everything in explorer mode, otherwise the discovered ones. */
  private known(): Set<StageId> {
    return this.host.explorer() ? new Set(ALL_STAGES) : new Set(this.host.game.profile.data.discovered);
  }

  show(current?: StageId): void {
    const { game } = this.host;
    const found = this.known();
    const discovered = new Set(game.profile.data.discovered);
    const endings = new Set(game.profile.data.endings);
    this.root.innerHTML = '';
    const top = el(
      'div',
      'codex-top',
      `<h2>${ICONS.tree}Evolution Tree${this.host.explorer() ? '<span class="xp-badge">Stage explorer</span>' : ''}</h2><div class="count">Forms discovered: <b>${discovered.size}</b> / 16 · Endings discovered: <b>${ENDING_STAGES.filter((e) => endings.has(e)).length}</b> / ${ENDING_STAGES.length}</div>`,
    );
    const body = el('div', 'codex-body');
    const wrap = el('div', 'tree-wrap codex-grid');
    wrap.innerHTML = buildTreeSvg({ found, endings, current, highlight: current ? [...game.run.data.history] : undefined, branchLabels: true });
    wrap.querySelectorAll<SVGGElement>('.node').forEach((n) => {
      const id = n.dataset.id as StageId;
      n.addEventListener('click', () => {
        if (this.selected === id && found.has(id)) {
          this.host.startAt(id);
          return;
        }
        this.select(id);
        game.audio.sfx('ui_click', { volume: 0.6 });
      });
      n.addEventListener('navfocus', () => this.select(id));
    });
    body.append(wrap, this.detail);
    const back = navButton(`${ICONS.back}Back`, 'small ghost codex-back', () => this.host.onClose());
    top.prepend(back);
    this.root.append(top, body);
    this.root.classList.add('visible');
    this.isOpen = true;

    this.pedestal ??= new PedestalScene({ assets: game.assets, quality: game.quality });
    this.layoutPedestal();
    this.host.pushAux(this.pedestal);
    this.host.nav.push({ root: this.root, onBack: () => this.host.onClose() }, false);
    const first = current ?? (found.has('human') ? 'human' : [...found].pop() ?? 'cell');
    this.select(first);
    requestAnimationFrame(() => {
      const n = wrap.querySelector<HTMLElement>(`.node[data-id="${first}"]`);
      if (n) this.host.nav.setFocus(n, false);
    });

    // Drag to spin the preview.
    let drag: { x: number; yaw: number } | null = null;
    this.detail.onpointerdown = (e) => {
      if ((e.target as HTMLElement).closest('button')) return;
      drag = { x: e.clientX, yaw: this.pedestal!.dragYaw };
    };
    this.detail.onpointerup = this.detail.onpointerleave = () => (drag = null);
    this.detail.onpointermove = (e) => {
      if (drag && this.pedestal) this.pedestal.dragYaw = drag.yaw + (e.clientX - drag.x) * 0.012;
    };
  }

  private layoutPedestal(): void {
    if (!this.pedestal) return;
    const short = window.innerHeight < 500;
    this.pedestal.screenX = short ? 0.81 : 0.79;
    this.pedestal.screenY = short ? 0.33 : 0.36;
  }

  private select(id: StageId): void {
    if (this.selected === id && this.detail.childElementCount) return;
    this.selected = id;
    const { game } = this.host;
    const found = this.known();
    const endings = new Set(game.profile.data.endings);
    const n = TREE[id];
    const isFound = found.has(id);
    this.root.querySelectorAll('.tree-wrap .node').forEach((x) => x.classList.toggle('sel', (x as HTMLElement).dataset.id === id));
    this.detail.classList.remove('enter');
    void this.detail.offsetWidth;
    this.detail.classList.add('enter');
    this.detail.innerHTML = '';
    const kind = n.kind === 'final' ? 'True ending' : n.kind === 'terminal' ? 'Ending' : 'Stage';
    const best = game.profile.data.bestTimes[id];
    this.detail.append(
      el('div', 'cd-era', isFound ? escapeHtml(n.era) : 'Unknown era'),
      el('div', 'cd-name', isFound ? escapeHtml(n.name) : '???'),
    );
    (this.detail.lastChild as HTMLElement).style.color = isFound ? n.color : 'var(--ink-faint)';
    this.detail.append(el('div', 'cd-tag', isFound ? escapeHtml(n.tagline) : 'Evolve along the right branch to discover this form.'));
    const meta = el('div', 'cd-meta');
    meta.innerHTML =
      `<span class="${n.kind !== 'stage' ? 'ending' : ''}">${n.kind !== 'stage' ? ICONS.star : ICONS.dna}${kind}${n.kind !== 'stage' && endings.has(id) ? ' · found' : ''}</span>` +
      (isFound ? `<span>${ICONS.clock}~${n.targetMinutes} min</span>` : '') +
      (best ? `<span>${ICONS.clock}Best ${fmtTime(best)}</span>` : '');
    this.detail.append(meta);
    if (isFound && n.paths.length) {
      const paths = el('div', 'cd-paths');
      paths.innerHTML = n.paths
        .map((p) => {
          const known = found.has(p.to);
          return `<span><i style="color:${p.color}">${BRANCH_ICONS[p.key] ?? ICONS.dna}</i><b style="color:${p.color}">${escapeHtml(p.label)}</b> → ${known ? escapeHtml(TREE[p.to].name) : '???'} <small>· ${escapeHtml(p.hint)}</small></span>`;
        })
        .join('');
      this.detail.append(paths);
    }
    if (isFound) {
      const btns = el('div', 'cd-btns');
      btns.append(navButton(`${ICONS.play}Play from here`, 'primary small', () => this.host.startAt(id)));
      this.detail.append(btns);
    } else {
      this.detail.append(el('div', 'cd-lock', `${ICONS.lock}Undiscovered`));
    }
    void this.pedestal?.show(id, { silhouette: !isFound });
  }

  hide(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove('visible');
    this.host.nav.remove(this.root);
    this.host.popAux();
  }
}
